/**
 * 기록 — 리포트. (활동 로그는 2026-10-21 부터 시스템 계정 메뉴라 고객용 설명서에 두지 않는다.)
 */
import type { ManualChapter } from '../types.js';

export const REPORTS: ManualChapter = {
  slug: 'reports',
  title: '리포트',
  summary: '어떤 콘텐츠·캠페인이 어느 화면·위치에서 몇 번, 얼마 동안 나갔는지 집계하고 엑셀·인쇄용 보고서로 냅니다.',
  menuPath: '/reports',
  capability: 'report:read',
  sections: [
    {
      id: 'view',
      title: '재생 실적 보기',
      blocks: [
        {
          kind: 'text',
          text: '화면은 실제로 재생한 기록(**재생 증빙**)을 주기적으로 서버에 올립니다. 리포트는 그 기록을 모아 보여주므로, 광고주 정산이나 실제 재생 여부를 확인할 때 근거가 됩니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[리포트]] 메뉴를 엽니다.', body: '처음에는 최근 30일이 선택되어 있습니다.' },
            { title: '오른쪽 위에서 시작·종료 날짜를 고릅니다.', body: '고르는 즉시 다시 집계됩니다. 조회 단추는 따로 없습니다.' },
            { title: '집계 기준을 고릅니다.', body: '아래 표를 참고하세요.' },
            { title: '“총 재생 횟수”, “총 노출 시간”, “재생한 화면” 과 표를 확인합니다.', body: '막대는 가장 많이 나간 항목과 비교한 비율입니다. 위의 합계는 늘 기간 전체입니다 — 표가 1,000줄을 넘어 잘려도 합계는 줄지 않습니다.' },
          ],
        },
        {
          kind: 'table',
          header: ['집계 기준', '이럴 때'],
          rows: [
            ['콘텐츠별', '광고·홍보물마다 몇 번 나갔는지 — 정산'],
            ['캠페인별', '캠페인마다 몇 번, 몇 대의 화면에서 나갔는지 — 캠페인 결과 보고'],
            ['위치별', '사옥·매장(화면의 위치)마다 얼마나 나갔는지'],
            ['화면별', '어느 화면이 제대로 재생하고 있는지 — 현장 점검'],
            ['플레이리스트별', '편성 묶음마다 얼마나 나갔는지'],
            ['일자별', '날마다 얼마나 나갔는지 — 추세(날짜 순)'],
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '캠페인별은 “캠페인 기간 안에 그 캠페인의 플레이리스트로 나온 재생” 입니다',
          text: '화면은 어느 캠페인 덕에 틀었는지 따로 적지 않습니다. 그래서 캠페인의 기간과 플레이리스트로 셉니다. 같은 플레이리스트를 쓰는 캠페인끼리 기간이 겹치면 겹친 재생은 양쪽에 다 들어가, 줄을 더한 값이 합계보다 클 수 있습니다. 지운 캠페인은 줄이 없습니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '“(삭제된 화면)” 이라고 적힌 줄',
          text: '집계 기간 뒤에 화면을 철거하거나 콘텐츠를 지우면 이름이 남지 않습니다. 그때는 **“(삭제된 화면) 6a97ab81”** 처럼 무엇이 지워졌는지와 내부 번호 앞 8자를 적습니다 — 공급사에 그 번호를 알려 주면 언제 누가 지웠는지 되짚을 수 있습니다. 실적은 그대로 남아 있으니 정산에서 빠지지 않습니다. 내려받는 CSV 도 같은 규칙입니다.',
        },
      ],
    },
    {
      id: 'export',
      title: '파일로 내려받기',
      blocks: [
        {
          kind: 'text',
          text: '보고 목적에 맞게 셋 중 하나로 받습니다. 셋 다 화면에서 고른 기간을 그대로 씁니다.',
        },
        {
          kind: 'table',
          header: ['단추', '받는 것', '이럴 때'],
          rows: [
            ['[[엑셀 보고서]]', '요약 한 장과 콘텐츠별·캠페인별·위치별·화면별·플레이리스트별·일자별 시트(숫자는 숫자 칸)', '윗선·광고주에 올리는 보고서'],
            ['[[인쇄용 보고서]]', 'A4 한 장에 요약·콘텐츠별·캠페인별·위치별(상위 20)·일자별. 브랜드 로고·이름이 머리에 들어갑니다', '회의 자료 · PDF 로 저장해 메일로'],
            ['[[CSV 원자료]]', '재생 한 번이 한 줄(시각·화면·콘텐츠·재생 시간·위치·플레이리스트·현지 시각)', '자체 분석 · 피벗 테이블'],
          ],
        },
        {
          kind: 'steps',
          title: '인쇄용 보고서를 PDF 로 저장하기',
          steps: [
            { title: '기간을 고른 뒤 [[인쇄용 보고서]] 를 누릅니다.', body: 'A4 모양의 보고서가 나타납니다.' },
            { title: '[[인쇄 · PDF 저장]] 을 누릅니다.' },
            { title: '인쇄 창에서 대상(프린터)을 “PDF로 저장” 으로 바꾸고 저장합니다.', body: '용지는 A4, 배율은 기본(100%)으로 둡니다. [[리포트로 돌아가기]] 로 표로 돌아옵니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: 'CSV 는 기간 안의 재생을 하나도 빼지 않습니다',
          text: '예전에는 최신 5만 줄에서 잘렸지만 지금은 기간 전체를 담습니다(기간은 최대 400일). 화면이 많고 기간이 길면 수백만 줄이 될 수 있어, 엑셀이 한 시트에 담는 104만 줄을 넘으면 기간을 나눠 받으세요. **played_at_local** 열이 브라우저의 시간대(한국이면 한국 시간)로 적은 시각입니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '받은 사실도 남습니다',
          text: '엑셀·CSV 를 받으면 변경 기록에 “재생 리포트 내려받기” 로 남습니다 — 누가 언제 실적 자료를 가져갔는지 알 수 있습니다.',
        },
      ],
    },
    {
      id: 'empty',
      title: '기록이 비어 있을 때',
      blocks: [
        {
          kind: 'faq',
          items: [
            {
              q: '“이 기간에 재생 기록이 없습니다” 라고 나옵니다.',
              a: '화면이 온라인인지, 편성이 배정되어 있는지 확인하세요. 화면이 오프라인이던 동안의 기록은 다시 연결된 뒤에 올라오므로 늦게 보일 수 있습니다.',
            },
            {
              q: '방금 재생한 기록이 아직 보이지 않습니다.',
              a: '화면은 기록을 모아서 주기적으로 올립니다. 잠시 뒤에 다시 보세요.',
            },
          ],
        },
      ],
    },
  ],
};

